import type { ReactNode } from 'react';

import { Anchor, List, Stack, Text } from '@mantine/core';

export type FAQItem = {
  answer: ReactNode;
  answerPlain: string;
  question: string;
  value: string;
};

export const FAQ_ITEMS: FAQItem[] = [
  {
    value: 'what-is-assay-for',
    question: 'What is Assay for?',
    answerPlain:
      "Assay is a personal CRM for staying in touch with the people who matter - clients, collaborators, former colleagues, the friends of your business. You import the people you already know, sort them by how close you are, and Assay reminds you when it's time to reconnect, then drafts a personal message so reaching out takes seconds rather than willpower. It is built for genuine one-to-one relationships, not for blasting a list.",
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Assay is a personal CRM for staying in touch with the people who matter - clients,
          collaborators, former colleagues, the friends of your business. You import the people you
          already know, sort them by how close you are, and Assay reminds you when it&apos;s time to
          reconnect, then drafts a personal message so reaching out takes seconds rather than
          willpower.
        </Text>
        <Text size="sm" c="dimmed">
          It is built for genuine one-to-one relationships, not for blasting a list.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'what-assay-does-not-do',
    question: 'What Assay does not do',
    answerPlain:
      'Assay is not a cold-email sequencer, not a LinkedIn automation bot, and not a heavyweight sales CRM. It will not auto-send messages on your behalf or pretend a stranger is a close contact. You stay in control of who you reach out to, what gets sent, and when.',
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Assay is not a cold-email sequencer, not a LinkedIn automation bot, and not a heavyweight
          sales CRM. It will not auto-send messages on your behalf or pretend a stranger is a close
          contact.
        </Text>
        <Text size="sm" c="dimmed">
          You stay in control of who you reach out to, what gets sent, and when.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'how-ai-drafts-work',
    question: 'How do AI drafts actually work?',
    answerPlain:
      "Assay starts with your template, the contact record, your relationship tier, and the research it has already gathered. The AI personalises the draft so it's grounded in a real relationship instead of a generic script. If context is thin, Assay leaves placeholders rather than inventing facts. You still review, edit, and send the final message yourself, and capture what you actually said so that Assay can learn how your drafts change over time. That edit history feeds a weekly retrospective: a look at how consistently you're staying in touch and where your messages can improve. Over time, Assay gets better at writing in your voice.",
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Assay starts with your template, the contact record, your relationship tier, and the
          research it has already gathered. The AI personalises the draft so it's grounded in a real
          relationship instead of a generic script.
        </Text>
        <Text size="sm" c="dimmed">
          If context is thin, Assay leaves placeholders rather than inventing facts. You still
          review, edit, and send the final message yourself, and capture what you actually said so
          that Assay can learn how your drafts change over time.
        </Text>
        <Text size="sm" c="dimmed">
          That edit history feeds a weekly retrospective: a look at how consistently you&apos;re
          staying in touch and where your messages can improve. Over time, Assay gets better at
          writing in your voice.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'data-privacy',
    question: 'Where is my contact data stored, and who can see it?',
    answerPlain:
      "Contacts, grades, notes, and message history are stored in an encrypted database with row-level security, so your data is isolated to your workspace - no other users can access it. Your data is processed by Anthropic's API to generate drafts and run enrichment. We don't sell it or share it beyond what's needed to run the product. For full detail on what's processed and why, see our Privacy Policy at https://runassay.app/privacy.",
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Contacts, grades, notes, and message history are stored in an encrypted database with
          row-level security, so your data is isolated to your workspace - no other users can access
          it.
        </Text>
        <Text size="sm" c="dimmed">
          Your data is processed by Anthropic's API to generate drafts and run enrichment. We don't
          sell it or share it beyond what's needed to run the product. For full detail on what's
          processed and why, see our <Anchor href="/privacy">Privacy Policy</Anchor>.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'google-scopes',
    question: 'Does connecting Google let Assay read my email?',
    answerPlain:
      'No. Assay asks for a single Google scope - contacts.readonly - which lets it read your Google Contacts and nothing else. It cannot open your inbox, read message content, or see who you email, because that permission is never requested. What you get is the address book you have already curated, imported as contacts you can then grade. Everything about the relationship after that - calls, coffees, notes, check-ins - is what you log yourself. You can disconnect Google at any time from Account, and the contacts you have already imported stay put.',
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          No. Assay asks for a single Google scope -{' '}
          <Text component="span" ff="monospace" size="sm">
            contacts.readonly
          </Text>{' '}
          - which lets it read your Google Contacts and nothing else. It cannot open your inbox,
          read message content, or see who you email, because that permission is never requested.
        </Text>
        <Text size="sm" c="dimmed">
          What you get is the address book you have already curated, imported as contacts you can
          then grade. Everything about the relationship after that - calls, coffees, notes,
          check-ins - is what you log yourself.
        </Text>
        <Text size="sm" c="dimmed">
          You can disconnect Google at any time from Account, and the contacts you have already
          imported stay put.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'linkedin-csv',
    question: 'How do I export my LinkedIn connections as a CSV for Assay?',
    answerPlain:
      'LinkedIn changes the menu labels from time to time, but the flow is usually: open LinkedIn, then go to Settings & Privacy; find the Data privacy section and choose Get a copy of your data; request your Connections export and wait for LinkedIn to email the file; download the CSV, then upload it in Assay from the CSV Upload tab. Assay works best when the file includes name, email, company, and LinkedIn URL. If the export has extra columns, that is fine - Assay will ignore what it does not need.',
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          LinkedIn changes the menu labels from time to time, but the flow is usually:
        </Text>
        <List spacing={6} size="sm" c="dimmed">
          <List.Item>Open LinkedIn, then go to Settings &amp; Privacy.</List.Item>
          <List.Item>Find the Data privacy section and choose Get a copy of your data.</List.Item>
          <List.Item>
            Request your Connections export and wait for LinkedIn to email the file.
          </List.Item>
          <List.Item>Download the CSV, then upload it in Assay from the CSV Upload tab.</List.Item>
        </List>
        <Text size="sm" c="dimmed">
          Assay works best when the file includes name, email, company, and LinkedIn URL. If the
          export has extra columns, that is fine - Assay will ignore what it does not need.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'prospect-tier',
    question: 'What do I get on the free Trace tier?',
    answerPlain:
      'Trace is the free entry point for trying Assay properly. You can import contacts by CSV, sort your network, get stay-in-touch reminders, and generate AI-assisted check-in drafts without paying. The free tier includes up to 25 contacts and 10 check-ins per month. Google Contacts import and unlimited usage start on Seam.',
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Trace is the free entry point for trying Assay properly. You can import contacts by CSV,
          sort your network, get stay-in-touch reminders, and generate AI-assisted check-in drafts
          without paying.
        </Text>
        <Text size="sm" c="dimmed">
          The free tier includes up to 25 contacts and 10 check-ins per month. Gmail import and
          unlimited usage start on Seam.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'what-is-context',
    question: 'What is Context, and how does it work?',
    answerPlain:
      "Context is where you feed Assay unstructured material about the people in your network - the kind of stuff you would normally type into a notes field manually. You upload a file, Assay reads it, and it writes a structured summary back against the matching contact so that future drafts can draw on it. A few examples of what works well: meeting notes, a screenshot of an article or interview, a conference bio PDF, a LinkedIn post screenshot, or your LinkedIn messages CSV export. If it is about a person in your network and you want to remember it, it's probably good Context.",
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Context is where you feed Assay unstructured material about the people in your network -
          the kind of stuff you would normally type into a notes field manually. You upload a file,
          Assay reads it, and it writes a structured summary back against the matching contact so
          that future drafts can draw on it.
        </Text>
        <Text size="sm" c="dimmed">
          A few examples of what works well: meeting notes, a screenshot of an article or interview,
          a conference bio PDF, a LinkedIn post screenshot, or your LinkedIn messages CSV export. If
          it is about a person in your network and you want to remember it, it's probably good
          Context.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'context-match-confidence',
    question: 'How does Assay avoid mixing up two people with the same name?',
    answerPlain:
      'Every extracted piece of context is matched to your existing contacts through a hybrid rule-based + AI approach. Matches with high confidence are treated as definitive and auto-applied. Weaker matches are held in a review queue so you can pick the right one or reject the proposal. Nothing gets written to a contact record until you confirm, except for those definitive matches.',
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Every extracted piece of context is matched to your existing contacts through a hybrid
          rule-based + AI approach. Matches with high confidence are treated as definitive and
          auto-applied. Weaker matches are held in a review queue so you can pick the right one or
          reject the proposal.
        </Text>
        <Text size="sm" c="dimmed">
          Nothing gets written to a contact record until you confirm, except for those definitive
          matches.
        </Text>
      </Stack>
    ),
  },
  {
    value: 'context-privacy',
    question: 'Who can see the files I upload to Context?',
    answerPlain:
      'Files are stored securely and encrypted at rest and in-transit, read only by the extraction task that processes them, and deleted when you delete the upload or close your account. The content is sent to our AI provider during extraction under the same data terms as the rest of Assay (see the Privacy Policy at https://runassay.app/privacy).',
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Files are stored securely and encrypted at rest and in-transit, read only by the
          extraction task that processes them, and deleted when you delete the upload or close your
          account. The content is sent to our AI provider during extraction under the same data
          terms as the rest of Assay (see the <Anchor href="/privacy">Privacy Policy</Anchor>).
        </Text>
      </Stack>
    ),
  },
  {
    value: 'limits-and-upgrading',
    question: 'What happens when I hit a limit or decide to upgrade?',
    answerPlain:
      'If you hit the Trace cap, Assay pauses additional imports or marked-sent check-ins until you upgrade. Your existing contacts, grades, notes, and history stay exactly where they are - nothing gets deleted. Upgrading unlocks more volume immediately. Seam removes the contact and monthly check-in limits, and higher plans add the extra features shown on the pricing page.',
    answer: (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          If you hit the Trace cap, Assay pauses additional imports or marked-sent check-ins until
          you upgrade. Your existing contacts, grades, notes, and history stay exactly where they
          are - nothing gets deleted.
        </Text>
        <Text size="sm" c="dimmed">
          Upgrading unlocks more volume immediately. Seam removes the contact and monthly check-in
          limits, and higher plans add the extra features shown on the pricing page.
        </Text>
      </Stack>
    ),
  },
];
